import { useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import { Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import Yup from 'utils/validation/yup-extensions';
import AppWrappedLabel from '../AppWrappedLabel';
import AppWrappedSwitch from '../AppWrappedSwitch';
import AppWrappedTextbox from '../AppWrappedTextbox';
import FormSection from '../FormSection';
import InvisibilityCloak from '../InvisibilityCloak';
import {
	EPIC_FIELDS,
	EPIC_FIELD_KEYS,
	EPIC_COMPONENT_ID,
	EPIC_FIELD_OPTIONS,
	STATIC_DESCRIPTIONS,
} from './_const';
import ServiceAreaField from '../1616BridgesEpic/components/ServiceAreaField';
import DefaultDepartmentField from './components/DefaultDepartmentField';
import FunctionalRoleField from './components/FunctionalRoleField';
import UserCredentialField from './components/UserCredentialField';
import PositionField from './components/PositionField';
import {
	fetchPositionVisibilityRules,
	fetchFunctionalRoleWithApprovalVisibilityRules,
	fetchDepartmentData,
} from './_api';
import HybridContainer from '../1616BridgesEpic/components/HybridContainer';
import AlertMonitor from '../1616BridgesEpic/components/AlertMonitor';
import { theme } from '../../../../../components/layouts/theme';
import WithLoadingHandler from '../../../../../hoc/withLoadingHandler/withLoadingHandler';
import PersonMetadata from '../PersonMetadata';
import styles from '../ApplicationForm.module.scss';
import SchedulingRoleField from './components/SchedulingRoleField';
import FunctionalRoleHelperLinks from './components/FunctionalRoleHelperLinks';
import FormHelperText from '@mui/material/FormHelperText';
import SchedulingRoleCheckbox from './components/SchedulingRoleCheckbox';

const LegacyEpicNotice = ({
	severity = 'info',
	title = 'Important',
	onClose,
	sx,
	children,
}) => (
	<Alert
		severity={severity}
		onClose={onClose}
		sx={{
			borderStyle: 'solid',
			borderColor: 'black',
			borderWidth: 'thin',
			borderRadius: '4px',
			...sx,
		}}
	>
		<AlertTitle>{title}</AlertTitle>

		<div>
			In preparation for the Epic Bridges Go-Live, the Epic/EpicCare (Legacy WPA)
			IMS form will be updated to only allow requests for View Only access,
			effective September 24, 2026.
		</div>
		<div>
			After this change, any requests to modify existing user access or to
			request new user access that requires permissions beyond View Only
			should no longer be submitted through the Epic/EpicCare (Legacy WPA) IMS form.
			Managers should submit these requests by opening a support ticket through
			the EpicCare Support Form on the Epic Support Site.
			<ul>
				<li>Category: Security</li>
				<li>Issue: Security question/issue</li>
			</ul>
		</div>
		{children}
	</Alert>
);

/**
 * Epic/Epic Care (79) Custom Application Request Form
 * @param {Object} props application request form properties
 * @returns customized form
 */
const EpicEpicCare = props => {
	const formikRef = useRef();

	const requesteeUsername = useSelector(state => {
		const formIndex = Object.keys(state.apps.allForms).find(
			formIndex => state.apps.allForms[formIndex].id === props.appId
		);
		return formIndex ? state.apps.allForms[formIndex].model.Username : '';
	});

	const [affiliateDepartmentList, setAffiliateDepartmentList] =
		useState(null);

	const isModificationRequest = useSelector(state => {
		const formIndex = Object.keys(state.apps.appsData).find(
			formIndex => state.apps.appsData[formIndex].id === props.appId
		);

		return formIndex
			? state?.apps?.appsData[formIndex]?.Action === 'ModifyAccess'
			: false;
	});

	// Gets all departments that have F_IS_AFFILIATE == true
	const getAllDepartments = async () => {
		const results = await fetchDepartmentData('allDepartments');
		if (typeof results === 'object') {
			const options = Object.keys(results).map(key => ({
				key: key,
				departmentId: results[key].departmentId,
				isAffiliate: results[key].isAffiliate,
			}));
			setAffiliateDepartmentList(options);
		}
		return [];
	};

	useEffect(() => {
		getAllDepartments();
	}, []);

	const [initialized, setInitialized] = useState(false);

	const otherRequestedApps = useSelector(state => {
		const apps = {};
		Object.values(state.apps.allForms).forEach(form => {
			apps[form.id] = form.name;
		});
		return apps;
	});

	const hasSubmissionBeenAttempted = useSelector(
		state => state.apps.submissionAttempted
	);

	const [previousSessionValue, setPreviousSessionValue] = useState(undefined);
	const previousSessionData = useSelector(state => {
		const formIndex = Object.keys(state.apps.allFormsInitial).find(
			formIndex =>
				state?.apps?.allFormsInitial[formIndex]?.id === props.appId
		);
		const memberKey = formIndex
			? Object.keys(
				state?.apps?.allFormsInitial[formIndex]?.model?.members
			).find(
				k =>
					state.apps.allFormsInitial[formIndex].model.members[k]
						.id === EPIC_COMPONENT_ID
			)
			: null;
		const memberValue = memberKey
			? state.apps.allFormsInitial[formIndex].model.members[memberKey]
				.value
			: null;
		if (memberValue) {
			return memberValue;
		} else {
			return null;
		}
	});

	useEffect(() => {
		if (
			previousSessionData &&
			typeof previousSessionData === 'string' &&
			!!!previousSessionValue
		) {
			try {
				const obj = JSON.parse(previousSessionData);
				setPreviousSessionValue(obj);
			} catch {
				setPreviousSessionValue(null);
			}
		} else {
			setPreviousSessionValue(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [previousSessionData]);

	const [positionsToRenderDea, setPositionsToRenderDea] = useState([]);
	const [positionsToRenderLabel, setPositionsToRenderLabel] = useState([]);
	const [positionsToRenderAdditional, setPositionsToRenderAdditional] =
		useState([]);
	const [sShowProcessingNotice, setShowProcessingNotice] = useState(true);
	const [sShowLegacyEpicNotice, setShowLegacyEpicNotice] = useState(true);
	const [
		functionalRolesToRenderJustification,
		setFunctionalRolesToRenderJustification,
	] = useState([]);
	const [
		functionalRolesForSpecificTemplate,
		setFunctionalRolesForSpecificTemplate,
	] = useState([]);
	const [epicCareDepartments, setEpicCareDepartments] = useState([]);

	useEffect(() => {
		fetchPositionVisibilityRules(EPIC_FIELD_KEYS.DEA_NUMBER)
			.then(response => {
				setPositionsToRenderDea(response);
			})
			.catch(() => { });
		fetchPositionVisibilityRules(EPIC_FIELD_KEYS.POSITION_LABEL)
			.then(response => {
				setPositionsToRenderLabel(response);
			})
			.catch(() => { });
		fetchPositionVisibilityRules(EPIC_FIELD_KEYS.E_PRESCRIBING)
			.then(response => {
				setPositionsToRenderAdditional(response);
			})
			.catch(() => { });
		fetchFunctionalRoleWithApprovalVisibilityRules().then(response => {
			setFunctionalRolesToRenderJustification(response);
		});
		fetchFunctionalRoleWithApprovalVisibilityRules(true).then(response => {
			setFunctionalRolesForSpecificTemplate(response);
		});
	}, []);

	/**
	 * helper function to extract a specific property (key and value) from an object
	 * @param {string} propName name of property you'd like to get
	 * @param {object} primarySource additional object to look in first.  Default will be EPIC_FIELDS.  Used primarily to repopulate in Mod requests.
	 * @example
	 * const EPIC_FIELDS = {
	 *   field1: {
	 *     defaultValue: 'foo',
	 *     validationSchema: Yup.string()
	 *   },
	 *   field2: {
	 *     defaultValue: 100,
	 *     validationSchema: Yup.number()
	 *   }
	 * }
	 * extractData('defaultValue);
	 * //returns
	 * //{
	 * //  field1: {
	 * //    defaultValue: 'foo',
	 * //  },
	 * //  field2: {
	 * //    defaultValue: 100,
	 * //  }
	 * //}
	 *
	 * @returns an object containing only the property you requested
	 */
	const extractValues = (propName, primarySource = null) => {
		const obj = {};
		Object.entries(EPIC_FIELDS).forEach(([key, value]) => {
			if (!(key in obj)) {
				obj[key] =
					primarySource && key in primarySource
						? primarySource[key]
						: value[propName] ?? '';
			}
		});
		return obj;
	};

	/**
	 * extract validation schema defined in `EPIC_FIELDS`
	 */
	const validationSchema = Yup.object().shape(
		extractValues('validationSchema', null)
	);

	useEffect(() => {
		if (
			previousSessionValue !== undefined &&
			!!otherRequestedApps &&
			!!positionsToRenderDea &&
			!!positionsToRenderAdditional
		) {
			setInitialized(true);
		}
	}, [
		previousSessionValue,
		otherRequestedApps,
		positionsToRenderDea,
		positionsToRenderAdditional,
	]);

	/**
	 * OneContent's App ID
	 */
	const ONE_CONTENT_APP_ID = 164;

	/**
	 * Visibility check for children of Functional Role field
	 * @param {string} value form input to check
	 * @returns true for certain functional roles when OneContent is not also being requested
	 */
	const shouldShowOneContentMessage = value => {
		return (
			[
				'HB PA ED PATIENT ACCESS',
				'HB PA PATIENT ACCESS LEADERSHIP',
				'SCHEDULING CLINICAL USER',
				'HB PA PM CONFIDENTIAL FRONT DESK 1.5',
				'HB PATIENT ACCESS SCHEDULE W/ EDIT SLOT',
			].includes(value) && !(ONE_CONTENT_APP_ID in otherRequestedApps)
		);
	};

	/**
	 * Visibility check for children of Training field
	 * @param {string} value form input to check
	 * @returns true when training is not scheduled or completed
	 */
	const shouldShowNoTrainingMessage = value => {
		return value === 'No';
	};

	/**
	 * Visibility check for children of Use Model After field
	 * @param {string} value form input to check
	 * @returns true when user indicates they want to model roles after another
	 */
	const shouldShowModelAfterField = value => {
		return value === EPIC_FIELD_OPTIONS.MODEL_AFTER_YES;
	};

	/**
	 * Visibility check for children of Use Manual Selection field
	 * @param {string} value form input to check
	 * @returns true when user indicates they want to manually select account settings
	 */
	const shouldShowManualSelectionFields = value => {
		return value === EPIC_FIELD_OPTIONS.MODEL_AFTER_NO;
	};

	/**
	 * Visibility check for custom form error
	 * @param {string} value form input to check
	 * @returns true when at least on toggle for Epic access or EpicCare access have been "touched" and when neither toggle have been turned on
	 */
	const shouldShowEpicEpicCareCustomError = (
		touched1,
		touched2,
		value1,
		value2
	) => {
		return (touched1 || touched2) && !(value1 === true || value2 === true);
	};

	/**
	 * Visibility check for Epic-related fields
	 * @param {string} value form input to check
	 * @returns true when Epic access has been requested
	 */
	const shouldShowEpicFields = value => {
		return value === true;
	};

	/**
	 * Visibility check for EpicCare-related fields
	 * @param {string} value form input to check
	 * @returns true when EpicCare access has been requested
	 */
	const shouldShowEpicCareFields = value => {
		return value === true;
	};

	/**
	 * Visibility check for children of Position field
	 * @param {string} value form input to check
	 * @returns true when user selects one of several specific positions
	 */
	const shouldShowPositionFields = value => {
		return positionsToRenderAdditional.includes(value);
	};

	/**
	 * Visibility check for informational label of Position field
	 * @param {string} value form input to check
	 * @returns true when user selects one of several specific positions
	 */
	const shouldShowPositionLabel = value => {
		return positionsToRenderLabel.includes(value);
	};

	/**
	 * Visibility check for DEA Number field
	 * @param {string} value form input to check
	 * @returns true when user selects one of several specific positions
	 */
	const shouldShowDeaField = value => {
		return positionsToRenderDea.some(
			p => p.toLowerCase() === value.toLowerCase()
		);
	};

	/**
	 * Logical check for when Model After option should not be available
	 * @param {string} value form input to check
	 * @returns true when user indicates they have not completed or scheduled their training
	 */
	const shouldShowMirrorDisableLabel = value => {
		return value === 'No';
	};

	const shouldShowFunctionalRoleJustification = value => {
		return functionalRolesToRenderJustification.some(
			p => p.toLowerCase() === value.toLowerCase()
		);
	};

	const shouldShowFunctionalRoleCheckbox = value => {
		return functionalRolesForSpecificTemplate.some(
			p => p.toLowerCase() === value.toLowerCase()
		);
	};

	/**
	 * Process Epic Department search results to look for EpicCare departments
	 * @param {object} d Department Search Results
	 */
	const updateEpicCareDepartments = d => {
		if (typeof d === 'object' && !Array.isArray(d)) {
			setEpicCareDepartments(
				Object.values(d)
					.filter(x => x.isEpicCareOnly)
					.map(x => x.departmentId)
			);
		}
	};

	if (
		formikRef?.current?.values[EPIC_FIELD_KEYS.MODEL_AFTER_SHOW] ===
		'MODEL_AFTER'
	) {
		formikRef?.current?.setFieldValue(EPIC_FIELD_KEYS.FUNCTIONAL_ROLE, '');
		formikRef?.current?.setFieldValue(EPIC_FIELD_KEYS.SCHEDULING_ROLE, '');
		formikRef?.current?.setFieldValue(
			EPIC_FIELD_KEYS.FUNCTIONAL_ROLE_CHECKBOX,
			false
		);
		formikRef?.current?.setFieldValue(
			EPIC_FIELD_KEYS.CHANGE_REGISTRATION_BILLING_ACCESS,
			''
		);
		formikRef?.current?.setFieldValue(
			EPIC_FIELD_KEYS.CHANGE_SCHEDULING_ACCESS,
			''
		);

		formikRef?.current?.setFieldValue(EPIC_FIELD_KEYS.POSITION, '');
		formikRef?.current?.setFieldValue(EPIC_FIELD_KEYS.USER_CREDENTIAL, '');
		formikRef?.current?.setFieldValue(EPIC_FIELD_KEYS.E_PRESCRIBING, '');
	}

	if (
		formikRef?.current?.values[EPIC_FIELD_KEYS.MODEL_AFTER_SHOW] ===
		'MANUAL'
	) {
		formikRef?.current?.setFieldValue(EPIC_FIELD_KEYS.MODEL_AFTER, '');
	}

	if (isModificationRequest) {
		return (
			<ThemeProvider theme={theme}>
				<Formik
					innerRef={formikRef}
					initialValues={{
						modNotice: ''
					}}
					validateOnMount={true}
					validationSchema={Yup.object().shape({
						modNotice: Yup.string().required('Your request for Epic/EpicCare (Legacy WPA) cannot be completed through IMS. Please follow the instructions above.'),
					})}
				>
					{formik => (
						<HybridContainer
							formik={formik}
							hasSubmissionBeenAttempted={
								hasSubmissionBeenAttempted
							}
							fieldKeys={EPIC_FIELD_KEYS}
							{...props}
						>
							{formik.touched.modNotice && formik.errors.modNotice && (
								<FormHelperText error>
									{formik.errors.modNotice}
								</FormHelperText>
							)}
							<LegacyEpicNotice
								title='Notice'
								sx={{ mt: 2 }}
							>
								<div>
									You will not be able to submit this request with Epic/EpicCare (Legacy WPA) in your cart. Please remove this application from your request and proceed or cancel it altogether.
								</div>
							</LegacyEpicNotice>
						</HybridContainer>
					)}
				</Formik>
			</ThemeProvider>
		);
	}

	return (
		<ThemeProvider theme={theme}>
			{sShowProcessingNotice && (
				<Alert
					severity='warning'
					onClose={() => setShowProcessingNotice(false)}
					sx={{
						borderStyle: 'solid',
						borderColor: 'black',
						borderWidth: 'thin',
						borderRadius: '4px',
					}}
				>
					<AlertTitle>Notice</AlertTitle>
					Please allow at least 7-10 business days for processing.
				</Alert>
			)}
			{sShowLegacyEpicNotice && (
				<LegacyEpicNotice
					onClose={() => setShowLegacyEpicNotice(false)}
					sx={{ mt: 1 }}
				/>
			)}
			<Box
				className={styles['sticky']}
				sx={{ display: 'flex', justifyContent: 'flex-end' }}
			>
				<PersonMetadata collapsible target={requesteeUsername} />
			</Box>
			<WithLoadingHandler
				isLoading={!initialized}
				showChildren={initialized}
			>
				<Formik
					innerRef={formikRef}
					initialValues={extractValues(
						'defaultValue',
						previousSessionValue
					)}
					validationSchema={validationSchema}
				>
					{formik => (
						<HybridContainer
							formik={formik}
							hasSubmissionBeenAttempted={
								hasSubmissionBeenAttempted
							}
							fieldKeys={EPIC_FIELD_KEYS}
							{...props}
						>
							<Box
								sx={{
									display: 'flex',
									flexWrap: 'wrap',
									flexDirection: 'row',
								}}
							>
								<AlertMonitor formik={formik} />
								<FormSection
									label='Primary Information'
									id={'primary-section'}
									name={EPIC_FIELD_KEYS.SECTION_MAIN}
									fullWidth
									defaultOpen
								>
									<ServiceAreaField
										name={EPIC_FIELD_KEYS.SERVICE_AREAS}
										formik={formik}
										isRequired
									/>
									<DefaultDepartmentField
										name={
											EPIC_FIELD_KEYS.DEFAULT_DEPARTMENT
										}
										formik={formik}
										isRequired
										setDepartments={d =>
											updateEpicCareDepartments(d)
										}
									/>
								</FormSection>

								<InvisibilityCloak
									name={
										EPIC_FIELD_KEYS.SHOW_VALIDATION_MESSAGE_EPIC_EPIC_CARE
									}
									dependentValues={[
										formik?.touched[
										EPIC_FIELD_KEYS
											.TOGGLE_REGISTRATION
										],
										formik?.touched[
										EPIC_FIELD_KEYS
											.TOGGLE_AMBULATORY
										],
										formik?.values[
										EPIC_FIELD_KEYS
											.TOGGLE_REGISTRATION
										],
										formik?.values[
										EPIC_FIELD_KEYS
											.TOGGLE_AMBULATORY
										],
									]}
									handleCloak={setShow => {
										const dependentName1 =
											EPIC_FIELD_KEYS.TOGGLE_REGISTRATION;
										const dependentName2 =
											EPIC_FIELD_KEYS.TOGGLE_AMBULATORY;
										if (
											dependentName1 in
											formik?.values &&
											dependentName2 in formik?.values
										) {
											//if either toggle has been touched and neither are On
											const showTest =
												shouldShowEpicEpicCareCustomError(
													formik?.touched[
													dependentName1
													],
													formik?.touched[
													dependentName2
													],
													formik?.values[
													dependentName1
													],
													formik?.values[
													dependentName2
													]
												);
											const validationTest =
												shouldShowEpicEpicCareCustomError(
													true,
													true,
													formik?.values[
													dependentName1
													],
													formik?.values[
													dependentName2
													]
												);
											setShow(showTest);
											formik.setFieldValue(
												EPIC_FIELD_KEYS.SHOW_VALIDATION_MESSAGE_EPIC_EPIC_CARE,
												validationTest
											);
										}
									}}
								>
									<Alert
										severity='error'
										name={
											EPIC_FIELD_KEYS.VALIDATION_MESSAGE_EPIC_EPIC_CARE
										}
										sx={{
											borderStyle: 'solid',
											borderColor: 'black',
											borderWidth: 'thin',
											borderRadius: '4px',
										}}
									>
										<AlertTitle>
											Validation Notice
										</AlertTitle>
										<>
											Please request{' '}
											<em>
												"Access to Epic
												Registration, Scheduling,
												Professional Billing, and
												Hospital Billing"
											</em>
											,{' '}
											<em>
												"Access To EpicCare
												Ambulatory"
											</em>
											, or both.
										</>
									</Alert>
								</InvisibilityCloak>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										flexDirection: 'row',
									}}
								>
									<FormSection
										label='Epic Registration, Scheduling, and Billing'
										id={'epic-section'}
										name={
											EPIC_FIELD_KEYS.SECTION_REGISTRATION
										}
										defaultOpen
										halfWidth
									>
										<AppWrappedSwitch
											label='Request Access to Epic Registration, Scheduling, Professional Billing, and Hospital Billing'
											name={
												EPIC_FIELD_KEYS.TOGGLE_REGISTRATION
											}
											formik={formik}
										/>
										<InvisibilityCloak
											name={
												EPIC_FIELD_KEYS.SHOW_REGISTRATION_FIELDS
											}
											dependentValues={[
												formik?.values[
												EPIC_FIELD_KEYS
													.TOGGLE_REGISTRATION
												],
											]}
											handleCloak={setShow => {
												const dependentName =
													EPIC_FIELD_KEYS.TOGGLE_REGISTRATION;
												if (
													dependentName in
													formik?.values
												) {
													const showTest =
														shouldShowEpicFields(
															formik?.values[
															dependentName
															]
														);
													setShow(showTest);
													formik.setFieldValue(
														EPIC_FIELD_KEYS.SHOW_REGISTRATION_FIELDS,
														showTest
													);
												}
											}}
										>
											<Grid container>
												<>
													<InvisibilityCloak
														name={
															EPIC_FIELD_KEYS.SHOW_FUNCTIONAL_ROLE
														}
														dependentValues={[
															formik
																?.values[
															EPIC_FIELD_KEYS
																.CHANGE_REGISTRATION_BILLING_ACCESS
															],
														]}
														handleCloak={setShow => {
															const dependentName1 =
																EPIC_FIELD_KEYS.CHANGE_REGISTRATION_BILLING_ACCESS;

															if (
																isModificationRequest ===
																false
															) {
																setShow(
																	true
																);
																formik.setFieldValue(
																	EPIC_FIELD_KEYS.SHOW_FUNCTIONAL_ROLE,
																	true
																);
															} else if (
																dependentName1 in
																formik?.values
															) {
																let showTest =
																	null;
																if (
																	isModificationRequest
																) {
																	showTest =
																		formik?.values[
																			EPIC_FIELD_KEYS
																				.CHANGE_REGISTRATION_BILLING_ACCESS
																		]?.toLowerCase() ===
																			'yes'
																			? true
																			: false;
																} else {
																	showTest = true;
																}
																setShow(
																	showTest
																);
																formik.setFieldValue(
																	EPIC_FIELD_KEYS.SHOW_FUNCTIONAL_ROLE,
																	showTest
																);
															}
														}}
													>
														<>
															<Grid
																item
																xs={12}
															>
																<FunctionalRoleField
																	name={
																		EPIC_FIELD_KEYS.FUNCTIONAL_ROLE
																	}
																	formik={
																		formik
																	}
																	isRequired
																	isDisabled
																/>
															</Grid>
														</>
													</InvisibilityCloak>
												</>

												<>
													<Grid item xs={12}>
														<SchedulingRoleField
															name={
																EPIC_FIELD_KEYS.SCHEDULING_ROLE
															}
															formik={
																formik
															}
															isRequired
															isDisabled
														/>
													</Grid>

													<InvisibilityCloak
														name={
															EPIC_FIELD_KEYS.SHOW_FUNCTIONAL_ROLE_CHECKBOX
														}
														dependentValues={[
															formik
																?.values[
															EPIC_FIELD_KEYS
																.SCHEDULING_ROLE
															],
															formik
																?.values[
															EPIC_FIELD_KEYS
																.CHANGE_REGISTRATION_BILLING_ACCESS
															],
														]}
														handleCloak={setShow => {
															const dependentName =
																EPIC_FIELD_KEYS.SCHEDULING_ROLE;

															const dependentName2 =
																EPIC_FIELD_KEYS.CHANGE_REGISTRATION_BILLING_ACCESS;

															var showTest = false;
															if (
																dependentName in
																formik?.values
															) {
																if (
																	dependentName2 in
																	formik?.values &&
																	formik
																		.values[
																	dependentName2
																	] ===
																	'No'
																) {
																	showTest = false;
																} else {
																	showTest =
																		shouldShowFunctionalRoleCheckbox(
																			formik
																				?.values[
																			dependentName
																			]
																		);
																}

																setShow(
																	showTest
																);
																formik.setFieldValue(
																	EPIC_FIELD_KEYS.SHOW_FUNCTIONAL_ROLE_CHECKBOX,
																	showTest
																);
															}
														}}
													>
														<Grid
															container
															direction='row-reverse'
															alignItems='center'
														>
															<Grid
																item
																xs={11}
															>
																<p
																	className={
																		styles[
																		'p-imagingdescription-text'
																		]
																	}
																>
																	<b>
																		{
																			STATIC_DESCRIPTIONS.FUNCTIONAL_ROLE_S102100024_PROVISIONING
																		}
																	</b>
																</p>
															</Grid>
															<Grid
																item
																xs={1}
															>
																<SchedulingRoleCheckbox
																	name={
																		EPIC_FIELD_KEYS.FUNCTIONAL_ROLE_CHECKBOX
																	}
																	label=''
																	formik={
																		formik
																	}
																/>
															</Grid>
														</Grid>
													</InvisibilityCloak>

													<FormHelperText>
														<FunctionalRoleHelperLinks />
													</FormHelperText>
												</>
											</Grid>

											{/* END */}
											<InvisibilityCloak
												name={
													EPIC_FIELD_KEYS.SHOW_ONE_CONTENT
												}
												dependentValues={[
													formik?.values[
													EPIC_FIELD_KEYS
														.FUNCTIONAL_ROLE
													],
												]}
												handleCloak={setShow => {
													const dependentName =
														EPIC_FIELD_KEYS.FUNCTIONAL_ROLE;
													if (
														dependentName in
														formik?.values
													) {
														const showTest =
															shouldShowOneContentMessage(
																formik
																	.values[
																dependentName
																]
															);

														setShow(showTest);
														formik.setFieldValue(
															EPIC_FIELD_KEYS.SHOW_ONE_CONTENT,
															showTest
														);
													}
												}}
											>
												<AppWrappedLabel
													name={
														EPIC_FIELD_KEYS.ONE_CONTENT
													}
													label='In order to scan via EPIC ILE, an additional request for OneContent Registration will need to be submitted.'
													formik={formik}
													warning
													isRequired
												/>
											</InvisibilityCloak>
										</InvisibilityCloak>
									</FormSection>

									<FormSection
										label='EpicCare Ambulatory'
										id={'epiccare-section'}
										name={
											EPIC_FIELD_KEYS.SECTION_AMBULATORY
										}
										defaultOpen
										halfWidth
									>
										<AppWrappedSwitch
											label='Request Access To EpicCare Ambulatory'
											name={
												EPIC_FIELD_KEYS.TOGGLE_AMBULATORY
											}
											formik={formik}
										/>
										<InvisibilityCloak
											name={
												EPIC_FIELD_KEYS.SHOW_AMBULATORY_FIELDS
											}
											dependentValues={[
												formik?.values[
												EPIC_FIELD_KEYS
													.TOGGLE_AMBULATORY
												],
											]}
											handleCloak={setShow => {
												const dependentName =
													EPIC_FIELD_KEYS.TOGGLE_AMBULATORY;
												if (
													dependentName in
													formik?.values
												) {
													const showTest =
														shouldShowEpicCareFields(
															formik?.values[
															dependentName
															]
														);
													setShow(showTest);
													formik.setFieldValue(
														EPIC_FIELD_KEYS.SHOW_AMBULATORY_FIELDS,
														showTest
													);
												}
											}}
										>
											<UserCredentialField
												name={
													EPIC_FIELD_KEYS.USER_CREDENTIAL
												}
												formik={formik}
											/>
											<PositionField
												name={
													EPIC_FIELD_KEYS.POSITION
												}
												formik={formik}
												isRequired
												isDisabled
											/>
											<InvisibilityCloak
												name={
													EPIC_FIELD_KEYS.SHOW_POSITION_LABEL
												}
												dependentValues={[
													formik?.values[
													EPIC_FIELD_KEYS
														.POSITION
													],
												]}
												handleCloak={setShow => {
													const dependentName1 =
														EPIC_FIELD_KEYS.POSITION;

													if (
														dependentName1 in
														formik?.values
													) {
														let showTest =
															shouldShowPositionLabel(
																formik
																	?.values[
																dependentName1
																]
															);
														setShow(showTest);
														formik.setFieldValue(
															EPIC_FIELD_KEYS.SHOW_POSITION_LABEL,
															showTest
														);
													}
												}}
											>
												<AppWrappedLabel
													name={
														EPIC_FIELD_KEYS.POSITION_LABEL
													}
													label={
														<>
															A{' '}
															<a
																href='https://upmc.service-now.com/esc?id=sc_cat_item&sys_id=7d8936dfc3bcee10ae9b522ed4013155'
																rel='noreferrer'
																target='_blank'
															>
																Provider
																Form
															</a>{' '}
															must be
															completed and
															submitted for
															this user.
															Failure to
															submit this form
															will delay
															completion of
															this request.
														</>
													}
													formik={formik}
												/>
											</InvisibilityCloak>
											<InvisibilityCloak
												name={
													EPIC_FIELD_KEYS.SHOW_DEA_NUMBER
												}
												dependentValues={[
													formik?.values[
													EPIC_FIELD_KEYS
														.POSITION
													],
												]}
												handleCloak={setShow => {
													const dependentName1 =
														EPIC_FIELD_KEYS.POSITION;

													if (
														dependentName1 in
														formik?.values
													) {
														let showTest =
															shouldShowDeaField(
																formik
																	?.values[
																dependentName1
																]
															);
														setShow(showTest);
														formik.setFieldValue(
															EPIC_FIELD_KEYS.SHOW_DEA_NUMBER,
															showTest
														);
													}
												}}
											>
												<AppWrappedTextbox
													name={
														EPIC_FIELD_KEYS.DEA_NUMBER
													}
													isRequired
													label='DEA Number'
													formik={formik}
												/>
											</InvisibilityCloak>

											<InvisibilityCloak
												name={
													EPIC_FIELD_KEYS.SHOW_POSITION_FIELDS
												}
												dependentValues={[
													formik?.values[
													EPIC_FIELD_KEYS
														.POSITION
													],
												]}
												handleCloak={setShow => {
													const dependentName =
														EPIC_FIELD_KEYS.POSITION;
													if (
														dependentName in
														formik?.values
													) {
														const showTest =
															shouldShowPositionFields(
																formik
																	?.values[
																dependentName
																]
															);

														setShow(showTest);
														formik.setFieldValue(
															EPIC_FIELD_KEYS.SHOW_POSITION_FIELDS,
															showTest
														);
													}
												}}
											>
												<AppWrappedTextbox
													name={
														EPIC_FIELD_KEYS.E_PRESCRIBING
													}
													label='Mirror Provider for Pool Settings'
													formik={formik}
												/>
											</InvisibilityCloak>
										</InvisibilityCloak>
									</FormSection>
								</Box>
								<FormSection
									name={EPIC_FIELD_KEYS.SECTION_OTHER}
									id={'other-section'}
									fullWidth
									defaultOpen
								>
									<AppWrappedTextbox
										name={EPIC_FIELD_KEYS.COMMENTS}
										label='Comments / Special Instructions'
										helperText={
											<>
												Please use{' '}
												<strong>
													Registration/Billing,
													Scheduling
												</strong>{' '}
												and/or <strong>Position</strong>{' '}
												to select user security as we
												cannot grant access based on
												comments.
											</>
										}
										formik={formik}
										multiline
										maxRows={3}
										maxLength={100}
									/>
								</FormSection>
							</Box>
						</HybridContainer>
					)}
				</Formik>
			</WithLoadingHandler>
		</ThemeProvider>
	);
};
export default EpicEpicCare;
